/**
 * DEGAP Cycle Store
 * 
 * Manages DEGAP cycles (containers) that go through D-E-G-A-P phases.
 * Based on DEGAP_FULL_ORCHESTRATION_SPEC.md
 * 
 * A cycle is a time-bound container for items being prioritized, guiding them through:
 * DECIDE → ENGAGE → GATHER → ARRANGE → PRIORITIZE
 * 
 * HYBRID PERSISTENCE:
 * - Primary: Supabase (synced to cloud for multi-device/multi-user access)
 * - Cache: localStorage (for offline access and quick reload)
 */

import { create } from 'zustand';
// Removed persist - Supabase is now the primary data store
import type { 
  DegapPhase, 
  DegapCycle, 
  CycleStatus,
  CycleRoleAssignment,
  CycleParticipant,
  ToolRunRecord,
  PhaseStatus,
  AggregationConfig,
  CycleCadence,
  StrategyRef,
} from '../../types/prioritization/degapCycle';
import { useStrategyStore } from './strategyStore';
import type { Json } from '../../types/database.generated';
// CyclePrerequisite type deprecated - now part of unified task model with TaskCategory
import { 
  PHASE_ORDER,
  PHASE_DEFINITIONS,
  createEmptyOutputs,
  canTransitionTo,
  getPhaseProgress,
  createInitialPhaseStatuses,
  DEFAULT_AGGREGATION_CONFIG,
  DEFAULT_CYCLE_CADENCE,
  canStartPhase,
  getActivePhases,
  getPrimaryPhase,
} from '../../types/prioritization/degapCycle';
import {
  fetchAll,
  fetchById,
  updateRecord,
  deleteRecord,
  isSupabaseAvailable,
  createInitialSyncState,
  generateUUID,
  getSafeSupabaseClient,
  fromDbFormat,
  toInsertRow,
  type SyncState,
} from '../../lib/supabaseSync';
import { alignDegapCycleLifecycleStatusWithPhase } from '../../lib/cycleLifecycleDisplayStatus';
import { checkPolicyForCycle } from '../../lib/authorization/storeEnforcement';
import { launchCycleDesign } from '../../lib/governedTransactions';
import { useAuditStore } from './auditStore';
import { mergeBaselineTemplatePolicyIntoMetadata } from '../../lib/baselineTemplatePlanner';
import { useTenantStore } from './tenantStore';
import {
  useSessionPolicyStore,
  decorateCycleWithPolicy,
} from './sessionPolicyStore';

const TABLE_NAME = 'degap_cycles';

/**
 * Phase C-2 decoration (canon 28 §6, migration 598): cycle Session Policy
 * data lives on session_policies ('cycle' scope). Synthesize the policy keys
 * back into metadata.sessionDefaults so existing readers
 * (cycleLayerFromCycle, CycleSessionDefaultsSection, bindingConflictDetector)
 * keep working. One query per fetch batch.
 */
async function decorateCyclesWithPolicies(cycles: DegapCycle[]): Promise<DegapCycle[]> {
  if (cycles.length === 0) return cycles;
  const policies = await useSessionPolicyStore
    .getState()
    .fetchPoliciesForScopes('cycle', cycles.map((c) => c.id));
  return cycles.map((c) => decorateCycleWithPolicy(c, policies.get(c.id)));
}

// Stable empty arrays to avoid creating new references in getters
const EMPTY_PHASES: DegapPhase[] = [];
const EMPTY_ITEM_IDS: string[] = [];

/**
 * Helper to sync a cycle to Supabase after mutation
 * Used by mutation methods to ensure all changes are persisted
 */
async function syncCycleToSupabase(
  cycle: DegapCycle,
  opts?: { includeLifecycleStatus?: boolean }
): Promise<void> {
  if (!isSupabaseAvailable()) return;

  try {
    const result = await updateRecord<DegapCycle>(TABLE_NAME, cycle.id, cycle, {
      includeDegapCycleStatus: opts?.includeLifecycleStatus === true,
    });
    if (result.error) {
      console.error(`[DegapProcessStore] Sync failed for cycle ${cycle.id}:`, result.error);
    }
  } catch (err) {
    console.error(`[DegapProcessStore] Sync exception for cycle ${cycle.id}:`, err);
  }
}

/**
 * Gate check result including EGAp iteration state
 */
interface CycleGateResult {
  canEnterEngage: boolean;
  canEnterGather: boolean;
  canEnterArrange: boolean;
  canEnterPrioritize: boolean;
  canComplete: boolean;
  blockers: string[];
  
  // EGAp iteration state
  egapIterationCount: number;
  activeEgapIterationId: string | null;
  activeEgapPhase: string | null;
  egapCriteriaLocked: boolean;
  egapValuesConverged: boolean;
  egapCandidatePoolLocked: boolean;
  egapProposalReady: boolean;
  
  // EGAp session counts
  egapAttributeSessionsComplete: number;
  egapValueSessionsComplete: number;
  egapPoolSessionsComplete: number;
  egapRankingSessionsComplete: number;
}

/**
 * Check cycle_progress view for gate satisfaction
 * Returns the can-enter flags for all phases plus EGAp iteration state
 */
async function checkCycleGates(cycleId: string): Promise<CycleGateResult | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  try {
    const { data, error } = await supabase
      .from('cycle_progress')
      .select('*')
      .eq('cycle_id', cycleId)
      .single();

    if (error || !data) {
      console.error('[DegapProcessStore] Failed to check gates:', error);
      return null;
    }

    // Compute blockers based on gate counts
    const blockers: string[] = [];
    if (!data.gate_d_owner) blockers.push('Cycle owner must be assigned');
    if (data.active_participant_count < 1) blockers.push('At least 1 active participant required');
    if (data.active_participants_with_rights < 1) blockers.push('At least 1 participant must have decision rights');
    if (data.confirmed_item_count < 1) blockers.push('At least 1 confirmed item required');
    
    // Check criteria locked from either legacy or EGAp iteration
    const criteriaLocked = data.criteria_locked || data.egap_criteria_locked;
    if (!criteriaLocked) blockers.push('Criteria must be locked');
    
    // Check pool locked from either legacy or EGAp iteration
    const poolLocked = data.pool_locked || data.egap_candidate_pool_locked;
    if (!poolLocked && data.can_enter_arrange) blockers.push('Candidate pool must be locked');
    
    if (data.ranking_sessions_complete < 1 && data.egap_ranking_sessions_complete < 1) {
      blockers.push('At least 1 ranking session must be completed');
    }
    if (data.snapshot_count < 1) blockers.push('At least 1 priority snapshot required');
    if (data.focus_list_count < 1) blockers.push('At least 1 focus list required');

    return {
      canEnterEngage: data.can_enter_engage,
      canEnterGather: data.can_enter_gather,
      canEnterArrange: data.can_enter_arrange,
      canEnterPrioritize: data.can_enter_prioritize,
      canComplete: data.can_complete,
      blockers,
      
      // EGAp iteration state
      egapIterationCount: data.egap_iteration_count || 0,
      activeEgapIterationId: data.computed_active_iteration_id || null,
      activeEgapPhase: data.active_egap_phase || null,
      egapCriteriaLocked: data.egap_criteria_locked || false,
      egapValuesConverged: data.egap_values_converged || false,
      egapCandidatePoolLocked: data.egap_candidate_pool_locked || false,
      egapProposalReady: data.egap_proposal_ready || false,
      
      // EGAp session counts
      egapAttributeSessionsComplete: data.egap_attribute_sessions_complete || 0,
      egapValueSessionsComplete: data.egap_value_sessions_complete || 0,
      egapPoolSessionsComplete: data.egap_pool_sessions_complete || 0,
      egapRankingSessionsComplete: data.egap_ranking_sessions_complete || 0,
    };
  } catch (err) {
    console.error('[DegapProcessStore] Gate check exception:', err);
    return null;
  }
}

export type DegapCycleId = string;

/** Returned by `createCycle` so UIs can show real DB/RLS messages instead of a generic failure. */
export type CreateCycleResult =
  | {
      ok: true;
      id: DegapCycleId;
      /**
       * The cycle row exists, but a follow-up setup step failed (decision-loop
       * seeding, prioritization context, configuration link). Non-empty means
       * "created, but not fully set up" — UIs must say so, not show plain success.
       */
      warnings?: string[];
    }
  | { ok: false; error: string; code?: 'POLICY_DENIED' | 'INSERT_FAILED' | 'SUPABASE_UNAVAILABLE' };

// Re-export for backward compatibility
export type { DegapPhase };
export type DegapProcess = DegapCycle;
export type DegapProcessId = DegapCycleId;

interface DegapProcessState {
  cycles: DegapCycle[];
  activeCycleId: DegapCycleId | null;
  
  // Sync state
  syncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: () => Promise<void>;
  /** Load one cycle by id (for setup/detail routes without refetching the full list). */
  fetchCycleById: (id: DegapCycleId) => Promise<{ success: boolean; error?: string }>;
  syncToSupabase: (
    cycle: DegapCycle,
    opts?: { includeLifecycleStatus?: boolean }
  ) => Promise<{ success: boolean; error?: string }>;

  // Cycle CRUD
  createCycle: (data: {
    name: string;
    description?: string;
    createdBy: string;
    cadence?: CycleCadence;
    orgScopeNodeId?: string;
    workflowId?: string | null;
    configurationId?: string | null;
    strategyRef?: StrategyRef;
    /** Owner ruling #9 (mig 767): given to the cycle's tracks created without an owner. */
    defaultTrackOwnerId?: string | null;
    /** Optional dates — stored in metadata.designerDateRange, where the Designer reads them. */
    dateRange?: { start: string; end: string };
    /**
     * The cycle template chosen for this cycle (mig 1040). Applied once by the
     * server as the cycle is made; without it the workflow's template applies.
     */
    templateId?: string | null;
  }) => Promise<CreateCycleResult>;
  createQuickExercise: (data: {
    name: string;
    description?: string;
    createdBy: string;
  }) => Promise<DegapCycleId>;
  /**
   * Re-apply the cached session_policies 'cycle' row onto the local cycle after
   * sessionPolicyStore.upsertPolicy, so the screen shows the save. Mirrors
   * trackStore.applyPolicyToTrackLocal.
   */
  applyPolicyToCycleLocal: (id: DegapCycleId) => void;

  updateCycle: (id: DegapCycleId, updates: Partial<Pick<DegapCycle, 'name' | 'description' | 'tags' | 'metadata' | 'workflowId' | 'strategyRef'>>) => Promise<{ success: boolean; error?: string }>;
  deleteCycle: (id: DegapCycleId) => Promise<{ success: boolean; error?: string }>;
  setActiveCycle: (id: DegapCycleId | null) => void;
  getCycle: (id: DegapCycleId) => DegapCycle | undefined;
  getActiveCycle: () => DegapCycle | undefined;

  // Phase management (legacy single-phase)
  /**
   * @deprecated Use transitionPhaseWithGates() instead. This method now delegates to transitionPhaseWithGates()
   * to enforce cycle_progress gates. Direct bypass of gate checks is no longer allowed per Window 4 invariants.
   */
  transitionPhase: (id: DegapCycleId, targetPhase: DegapPhase, userId: string, notes?: string) => 
    Promise<{ success: boolean; error?: string; missing?: string[] }>;
  canAdvancePhase: (id: DegapCycleId) => { allowed: boolean; reason?: string; missing?: string[] };
  getPhaseDefinition: (phase: DegapPhase) => typeof PHASE_DEFINITIONS[DegapPhase];
  getPhaseProgress: (id: DegapCycleId) => Record<DegapPhase, number> | undefined;
  
  // Gate-enforced phase transition (async, checks cycle_progress view)
  // This is the canonical phase transition method - all transitions must go through here
  transitionPhaseWithGates: (id: DegapCycleId, targetPhase: DegapPhase, userId: string, notes?: string) => 
    Promise<{ success: boolean; error?: string; blockers?: string[] }>;

  // Phase management (Core B - parallel phases)
  startPhase: (id: DegapCycleId, phase: DegapPhase, userId: string) => 
    Promise<{ success: boolean; error?: string }>;
  completePhase: (id: DegapCycleId, phase: DegapPhase, userId: string) => 
    Promise<{ success: boolean; error?: string }>;
  getActivePhases: (id: DegapCycleId) => DegapPhase[];
  getPhaseStatus: (id: DegapCycleId, phase: DegapPhase) => PhaseStatus | undefined;
  updatePhaseProgress: (id: DegapCycleId, phase: DegapPhase, progress: number) => Promise<void>;

  // Aggregation configuration (Core B)
  setAggregationConfig: (id: DegapCycleId, config: Partial<AggregationConfig>) => Promise<void>;
  setCycleOwner: (id: DegapCycleId, ownerId: string) => Promise<void>;

  // Status management
  /**
   * Start a draft cycle — the same governed launch the Cycle Designer uses
   * (launch_cycle_design, start_only; migration 881). Refusals come back, never throw.
   */
  startCycle: (id: DegapCycleId) => Promise<{ ok: boolean; error?: string }>;
  /** Resume a paused cycle (paused → active). Starting a draft is startCycle. */
  resumeCycle: (id: DegapCycleId) => Promise<void>;
  pauseCycle: (id: DegapCycleId) => Promise<void>;
  completeCycle: (id: DegapCycleId) => Promise<void>;
  archiveCycle: (id: DegapCycleId) => Promise<void>;

  // Item management
  addItemsToCycle: (id: DegapCycleId, itemIds: string[], options?: { userId?: string; inclusionReason?: string; note?: string }) => Promise<void>;
  removeItemsFromCycle: (id: DegapCycleId, itemIds: string[], options?: { userId?: string; reason?: string }) => Promise<void>;
  getCycleItems: (id: DegapCycleId) => string[];
  
  // EGAp Iteration management
  /**
   * Get or create an active EGAp iteration for a cycle.
   * If no active iteration exists, creates one.
   * Per the parent-child model, all sessions should belong to an iteration.
   */
  getOrCreateActiveIteration: (id: DegapCycleId, createdBy: string) => Promise<string>;
  
  /**
   * Get cycle gate status including EGAp iteration state.
   * Returns the gate check result from cycle_progress view.
   */
  getCycleGateStatus: (id: DegapCycleId) => Promise<CycleGateResult | null>;

  // Phase outputs - DECIDE
  addOutcomeDriver: (id: DegapCycleId, driver: string) => Promise<void>;
  removeOutcomeDriver: (id: DegapCycleId, driver: string) => Promise<void>;
  importDriversFromStrategy: (id: DegapCycleId) => Promise<{ imported: number; strategyName?: string }>;
  setClarityDocument: (id: DegapCycleId, document: string) => Promise<void>;
  setDomainBoundaries: (id: DegapCycleId, boundaries: string) => Promise<void>;
  addRoleAssignment: (id: DegapCycleId, assignment: Omit<CycleRoleAssignment, 'assignedAt'>) => void;

  // Phase outputs - ENGAGE
  addParticipant: (id: DegapCycleId, participant: Omit<CycleParticipant, 'joinedAt'>) => Promise<void>;
  removeParticipant: (id: DegapCycleId, participantUserId: string) => Promise<void>;
  addScheduledSession: (id: DegapCycleId, sessionId: string) => Promise<void>;
  setDecisionRights: (id: DegapCycleId, rights: string) => Promise<void>;

  // Phase outputs - GATHER
  markItemShaped: (id: DegapCycleId, itemId: string) => Promise<void>;
  updateGatherStats: (id: DegapCycleId) => Promise<void>;

  // Phase outputs - ARRANGE
  addFocusList: (id: DegapCycleId, focusListId: string) => Promise<void>;
  recordToolRun: (id: DegapCycleId, toolRun: Omit<ToolRunRecord, 'id' | 'runAt'>) => Promise<void>;
  addSnapshot: (id: DegapCycleId, snapshotId: string) => Promise<void>;

  // Phase outputs - PRIORITIZE
  publishSnapshot: (id: DegapCycleId, snapshotId: string) => Promise<void>;
  setCutLine: (id: DegapCycleId, position: number) => Promise<void>;

  // Backward compatibility aliases
  processes: DegapCycle[];
  activeProcessId: DegapCycleId | null;
  createProcess: (data: { name: string; description?: string; ownerId: string }) => Promise<CreateCycleResult>;
  updateProcess: (id: DegapCycleId, updates: Partial<DegapCycle>) => void;
  deleteProcess: (id: DegapCycleId) => void;
  setActiveProcess: (id: DegapCycleId | null) => void;
  getProcess: (id: DegapCycleId) => DegapCycle | undefined;
  getActiveProcess: () => DegapCycle | undefined;
  canTransitionPhase: (id: DegapCycleId, targetPhase: DegapPhase) => boolean;
  addItemsToProcess: (id: DegapCycleId, itemIds: string[]) => void;
  removeItemsFromProcess: (id: DegapCycleId, itemIds: string[]) => void;
  getProcessItems: (id: DegapCycleId) => string[];
}

function hydrateCycleOrgScope(cycle: DegapCycle): DegapCycle {
  if (cycle.orgScopeNodeId) return cycle;

  const metadataScope =
    typeof cycle.metadata === 'object' && cycle.metadata !== null
      ? (cycle.metadata as Record<string, unknown>).orgScopeNodeId
      : undefined;
  const bindingScope = cycle.orgUnitBinding?.primaryOrgUnitId;
  const orgScopeNodeId =
    (typeof metadataScope === 'string' && metadataScope.length > 0 ? metadataScope : undefined) ??
    bindingScope;

  return orgScopeNodeId ? { ...cycle, orgScopeNodeId } : cycle;
}

/**
 * G03: Flag in-progress sessions as abandoned when their parent cycle
 * transitions to 'completed' or 'archived'. Canon §G03.
 *
 * Only DRAFT and CRITERIA_FINALIZED sessions are affected — sessions that
 * have run or been published retain their results for historical record.
 */
async function flagCycleSessionsAbandoned(
  cycleId: string,
  reason: 'cycle_completed' | 'cycle_archived'
): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;

  const { data: activeSessions, error: fetchErr } = await supabase
    .from('prioritization_sessions')
    .select('id, state')
    .eq('cycle_id', cycleId)
    .in('state', ['DRAFT', 'CRITERIA_FINALIZED']);

  if (fetchErr) {
    console.warn('[DegapProcessStore] G03: could not fetch sessions for abandonment:', fetchErr.message);
    return;
  }
  if (!activeSessions || activeSessions.length === 0) return;

  const ids = activeSessions.map((s) => (s as { id: string }).id);
  const { error: updateErr } = await supabase
    .from('prioritization_sessions')
    .update({
      abandoned_at: new Date().toISOString(),
      abandoned_reason: reason,
      updated_at: new Date().toISOString(),
    })
    .in('id', ids);

  if (updateErr) {
    console.warn('[DegapProcessStore] G03: could not mark sessions abandoned:', updateErr.message);
    return;
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('sessions-abandoned', {
      detail: {
        cycleId,
        reason,
        count: ids.length,
        sessionIds: ids,
        message: `${ids.length} in-progress session(s) marked abandoned due to ${reason.replace('_', ' ')}.`,
      },
    }));
  }
}

export const useDegapProcessStore = create<DegapProcessState>()((set, get) => ({
      cycles: [],
      activeCycleId: null,
      syncState: createInitialSyncState(),

      // ======================================================================
      // Supabase Sync Actions
      // ======================================================================

      fetchFromSupabase: async () => {
        if (!isSupabaseAvailable()) {
          console.log('[DegapProcessStore] Supabase not available, using localStorage cache');
          return;
        }
        
        set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });
        
        const result = await fetchAll<DegapCycle>(TABLE_NAME, { column: 'created_at', ascending: false }, { filterByWorkspace: true, strictWorkspaceFilter: true });
        
        if (result.error) {
          set({ 
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              syncError: result.error 
            } 
          });
          console.error('[DegapProcessStore] Failed to fetch from Supabase:', result.error);
          return;
        }
        
        if (result.data) {
          const hydratedCycles = await decorateCyclesWithPolicies(
            result.data.map(hydrateCycleOrgScope)
          );
          // Stage 7 Slice 6: Do not resurrect local-only cycles — only DB-backed data (no false reality).
          set({ 
            cycles: hydratedCycles,
            syncState: { 
              ...get().syncState, 
              isLoading: false, 
              lastSyncAt: new Date(),
              syncError: null 
            }
          });
          console.log(`[DegapProcessStore] Loaded ${hydratedCycles.length} from Supabase (DB-only; no local-only merge).`);
        }
      },

      fetchCycleById: async (cycleId) => {
        const existing = get().getCycle(cycleId);
        if (existing) {
          return { success: true };
        }
        if (!isSupabaseAvailable()) {
          return { success: false, error: 'Supabase not configured' };
        }

        const result = await fetchById<DegapCycle>(TABLE_NAME, cycleId);
        if (result.error || !result.data) {
          return { success: false, error: result.error ?? 'Cycle not found' };
        }

        const [hydrated] = await decorateCyclesWithPolicies([
          hydrateCycleOrgScope(result.data),
        ]);
        set((state) => {
          const has = state.cycles.some((c) => c.id === cycleId);
          return {
            cycles: has
              ? state.cycles.map((c) => (c.id === cycleId ? hydrated : c))
              : [...state.cycles, hydrated],
          };
        });
        return { success: true };
      },
      
      syncToSupabase: async (cycle: DegapCycle, opts?: { includeLifecycleStatus?: boolean }) => {
        if (!isSupabaseAvailable()) {
          return { success: true };
        }
        
        set({ syncState: { ...get().syncState, isSyncing: true } });
        
        const result = await updateRecord<DegapCycle>(TABLE_NAME, cycle.id, cycle, {
          includeDegapCycleStatus: opts?.includeLifecycleStatus === true,
        });
        
        set({ syncState: { ...get().syncState, isSyncing: false } });
        
        if (result.error) {
          console.error('[DegapProcessStore] Failed to sync to Supabase:', result.error);
          return { success: false, error: result.error };
        }
        
        return { success: true };
      },

      // ======================================================================
      // Cycle CRUD
      // ======================================================================

      createCycle: async (data) => {
        const now = new Date();
        
        // Initialize phase statuses with DECIDE in_progress
        const initialPhaseStatuses = createInitialPhaseStatuses();
        initialPhaseStatuses.DECIDE = {
          ...initialPhaseStatuses.DECIDE,
          status: 'in_progress',
          startedAt: now,
          startedBy: data.createdBy,
        };
        
        // Snapshot the workspace's Baseline Template policy onto the cycle so
        // later admin edits don't retroactively change this cycle's tracks.
        const baselinePolicy = useTenantStore.getState().getCurrentWorkspace()?.baselineTemplatePolicy;
        const baseMetadataEntries: Record<string, unknown> = {};
        if (data.orgScopeNodeId) baseMetadataEntries.orgScopeNodeId = data.orgScopeNodeId;
        // Dates ride in with the create (one write) instead of a follow-up
        // updateCycle, which replaced metadata wholesale and dropped the
        // baseline policy snapshot below.
        if (data.dateRange && (data.dateRange.start || data.dateRange.end)) {
          baseMetadataEntries.designerDateRange = { start: data.dateRange.start, end: data.dateRange.end };
        }
        const baseMetadata: Record<string, unknown> | undefined =
          Object.keys(baseMetadataEntries).length > 0 ? baseMetadataEntries : undefined;
        const cycleMetadata = baselinePolicy
          ? mergeBaselineTemplatePolicyIntoMetadata(baseMetadata, baselinePolicy)
          : baseMetadata;

        const newCycle: DegapCycle = {
          id: generateUUID(),
          name: data.name,
          description: data.description,
          orgScopeNodeId: data.orgScopeNodeId,
          metadata: cycleMetadata,
          // Cycle mode and cadence (episodic, periodic, continuous)
          cadence: data.cadence || { ...DEFAULT_CYCLE_CADENCE },
          currentPhase: 'DECIDE',
          phaseHistory: [{
            phase: 'DECIDE',
            enteredAt: now,
            enteredBy: data.createdBy,
          }],
          // Core B: Parallel phase support
          phaseStatuses: initialPhaseStatuses,
          activePhases: ['DECIDE'],
          // Core B: Cycle ownership and aggregation
          ownerId: data.createdBy,
          aggregationConfig: { ...DEFAULT_AGGREGATION_CONFIG },
          itemIds: [],
          outputs: createEmptyOutputs(),
          status: 'draft',
          createdAt: now,
          createdBy: data.createdBy,
          updatedAt: now,
          sessionIds: [],
          itemPoolIds: [],
          snapshotIds: [],
          workflowId: data.workflowId ?? undefined,
          strategyRef: data.strategyRef,
          defaultTrackOwnerId: data.defaultTrackOwnerId || null,
        };

        const policyDecision = await checkPolicyForCycle('process.create', newCycle.id, newCycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.create:', reasonCode);
          const errMsg = `You can't create cycles with your current role or permissions (${String(reasonCode)}).`;
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'createCycle', message: errMsg, decision: policyDecision },
            }));

          }
          return { ok: false as const, error: errMsg, code: 'POLICY_DENIED' };
        }

        // Mig 762: one governed server transaction — the cycle row, its five
        // decision loops, its prioritization context (the P01 context
        // invariant), the configuration link and a cycle_created governance
        // event. Any failure writes nothing. The server sets creator/owner
        // (auth.uid()), status and phase; admins are notified by trigger.
        const supabase = getSafeSupabaseClient();
        if (isSupabaseAvailable() && supabase) {
          const cycleRow = toInsertRow(TABLE_NAME, newCycle);
          if (!cycleRow.workspace_id) {
            cycleRow.workspace_id = useTenantStore.getState().currentWorkspaceId ?? null;
          }
          if (data.templateId) cycleRow.template_id = data.templateId;
          const { data: res, error: rpcErr } = await supabase.rpc('create_cycle', {
            p_cycle: cycleRow as unknown as Json,
            p_configuration_id: data.configurationId ?? undefined,
          });
          const envelope = (res ?? null) as { success?: boolean; error?: string; code?: string; cycle?: Record<string, unknown> } | null;
          if (rpcErr || !envelope?.success) {
            const message =
              rpcErr?.message ||
              envelope?.error ||
              'The cycle could not be created. Nothing was saved.';
            console.error('[DegapProcessStore] create_cycle refused:', rpcErr ?? envelope);
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('cycle-create-failed', {
                detail: { message, code: envelope?.code ?? 'INSERT_FAILED' },
              }));
            }
            return { ok: false as const, error: message, code: envelope?.code === 'not_authorized' ? 'POLICY_DENIED' : 'INSERT_FAILED' };
          }

          const persisted: DegapCycle = envelope.cycle
            ? { ...newCycle, ...fromDbFormat<Partial<DegapCycle>>(envelope.cycle), phaseHistory: newCycle.phaseHistory, phaseStatuses: newCycle.phaseStatuses }
            : newCycle;
          set((state) => ({
            cycles: [...state.cycles, persisted],
          }));
          // The cycle's template (the one chosen, else its workflow's) was
          // applied by the server in the same transaction, once (mig 1040):
          // its people, groups and the seed every track takes when it is made.
          return { ok: true as const, id: persisted.id };
        }

        // No Supabase: do not create a fake persistent cycle (Build Contract: no false success).
        const offlineMsg =
          'Database not available — cycle was not created. Configure Supabase or sign in, then try again.';
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('cycle-create-failed', {
            detail: { message: offlineMsg, code: 'SUPABASE_UNAVAILABLE' },
          }));
        }
        return { ok: false as const, error: offlineMsg, code: 'SUPABASE_UNAVAILABLE' };
      },

      createQuickExercise: async (data) => {
        const created = await get().createCycle({
          name: data.name,
          description: data.description,
          createdBy: data.createdBy,
        });
        if (!created.ok) return '';

        const cycleId = created.id;

        // Patch uxMode onto the newly created cycle — both local state and DB.
        set((state) => ({
          cycles: state.cycles.map((c) =>
            c.id === cycleId ? { ...c, uxMode: 'quick_exercise' } : c
          ),
        }));

        const supabase = getSafeSupabaseClient();
        if (supabase) {
          await supabase
            .from(TABLE_NAME)
            .update({ ux_mode: 'quick_exercise' })
            .eq('id', cycleId);
        }

        return cycleId;
      },

      applyPolicyToCycleLocal: (id) => {
        const policy = useSessionPolicyStore.getState().getPolicy('cycle', id);
        set((state) => ({
          cycles: state.cycles.map((c) => {
            if (c.id !== id) return c;
            const defaults = { ...((c.metadata?.sessionDefaults ?? {}) as Record<string, unknown>) };
            delete defaults.timingMode;
            delete defaults.entryMode;
            delete defaults.defaultFacilitatorId;
            return decorateCycleWithPolicy({ ...c, metadata: { ...(c.metadata ?? {}), sessionDefaults: defaults } }, policy);
          }),
        }));
      },

      updateCycle: async (id, updates) => {
        const cycle = get().cycles.find(c => c.id === id);
        if (!cycle) return { success: false, error: 'Cycle not found' };
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (updateCycle):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'updateCycle', message: `Cannot update cycle: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return { success: false, error: reasonCode };
        }
        const updatedAt = new Date();
        const patch = { ...updates, updatedAt };
        const updated = { ...cycle, ...patch };

        // Stage 7 Slice 6: DB-first — persist then update state (no false reality).
        // PATCH only the fields being changed. Sending the full cycle object
        // includes client-only / non-column fields and can fail the Supabase
        // update — which silently dropped designer draft saves (path assignees).
        if (!isSupabaseAvailable()) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('store-warning', {
              detail: { message: 'Database not available. Cycle update was not saved.', type: 'error' },
            }));
          }
          return { success: false, error: 'Supabase not available' };
        }
        const result = await updateRecord<DegapCycle>(TABLE_NAME, id, patch);
        if (result.error) {
          console.error('[DegapProcessStore] Failed to update cycle in Supabase:', result.error);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('store-warning', {
              detail: { message: `Cycle update failed: ${result.error}`, type: 'error' },
            }));
          }
          return { success: false, error: result.error };
        }
        set((state) => ({
          cycles: state.cycles.map(c => c.id === id ? updated : c),
        }));
        // A cycle that never had a template applied takes its new workflow's
        // template now, once, on the server (mig 1040). Read the cycle back so
        // the template it recorded shows.
        if (updates.workflowId !== undefined && !cycle.templateAppliedAt) {
          try {
            await get().fetchCycleById(id);
          } catch (e) {
            console.warn('[DegapProcessStore] Re-read after workflow change failed:', e);
          }
        }
        return { success: true };
      },

      deleteCycle: async (id) => {
        const cycle = get().cycles.find(c => c.id === id);
        if (!cycle) return { success: false, error: 'Cycle not found' };

        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (deleteCycle):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'deleteCycle', message: `Cannot delete cycle: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return { success: false, error: reasonCode };
        }

        // Stage 7 Slice 6: DB-first — delete in DB then remove from state (no false reality).
        if (!isSupabaseAvailable()) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('store-warning', {
              detail: { message: 'Database not available. Cycle was not deleted.', type: 'error' },
            }));
          }
          return { success: false, error: 'Supabase not available' };
        }

        // What happens to the cycle's open sessions is the workspace's rule
        // (block | detach | cascade, canon 11 G01). The database applies it to
        // the delete itself (migration 1063) and refuses with the reason under
        // block; screens say it beforehand with cycleDeleteConsequence.
        const supabase = getSafeSupabaseClient();

        // Item pools still reference cycle_id via FK (legacy name: focus_lists_cycle_id_fkey).
        // Detach them so cycle delete succeeds even before ON DELETE SET NULL migration is applied.
        if (supabase) {
          const { error: poolDetachErr } = await supabase
            .from('item_pools')
            .update({ cycle_id: null, updated_at: new Date().toISOString() })
            .eq('cycle_id', id);
          if (poolDetachErr) {
            console.error('[DegapProcessStore] Failed to detach item pools from cycle:', poolDetachErr.message);
            return { success: false, error: `Cannot delete cycle: ${poolDetachErr.message}` };
          }
        }

        const result = await deleteRecord(TABLE_NAME, id);
        if (result.error) {
          console.error('[DegapProcessStore] Failed to delete cycle from Supabase:', result.error);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('store-warning', {
              detail: { message: `Cycle delete failed: ${result.error}`, type: 'error' },
            }));
          }
          return { success: false, error: result.error };
        }
        set((state) => ({
          cycles: state.cycles.filter(c => c.id !== id),
          activeCycleId: state.activeCycleId === id ? null : state.activeCycleId,
        }));
        return { success: true };
      },

      setActiveCycle: (id) => set({ activeCycleId: id }),

      getCycle: (id) => get().cycles.find(c => c.id === id),

      getActiveCycle: () => {
        const { activeCycleId, cycles } = get();
        return activeCycleId ? cycles.find(c => c.id === activeCycleId) : undefined;
      },

      // ======================================================================
      // Phase Management
      // ======================================================================
      // 
      // INVARIANT: Cycle state is authoritative for DEGAP phase.
      // This is the ONLY function that can advance cycle phases.
      // 
      // The following actions do NOT advance phases (per Doc 03):
      // - Catchball acceptance (Accept Provisional)
      // - Task completion
      // - Session state changes
      // - Governance state changes (Proposed → Decided)
      //
      // Phase transitions require explicit gate validation via canTransitionTo().
      // ======================================================================

      /**
       * @deprecated Use transitionPhaseWithGates() instead.
       * This method now delegates to transitionPhaseWithGates() to enforce cycle_progress gates.
       * Per Window 4 Invariant 1: Phase transitions must go through gate-checked paths.
       */
      transitionPhase: async (id, targetPhase, userId, notes) => {
        console.warn('[DegapProcessStore] transitionPhase() is deprecated. Use transitionPhaseWithGates() instead.');
        
        // Delegate to gate-enforced transition per Window 4 enforcement
        const result = await get().transitionPhaseWithGates(id, targetPhase, userId, notes);
        
        // Convert blockers to missing for backward compatibility
        return { 
          success: result.success, 
          error: result.error, 
          missing: result.blockers,
        };
      },

      canAdvancePhase: (id) => {
        const cycle = get().getCycle(id);
        if (!cycle) return { allowed: false, reason: 'Cycle not found' };

        console.log('[canAdvancePhase] Checking cycle:', {
          id: cycle.id,
          name: cycle.name,
          currentPhase: cycle.currentPhase,
          ownerId: cycle.ownerId,
          createdBy: cycle.createdBy,
        });

        const currentIndex = PHASE_ORDER.indexOf(cycle.currentPhase);
        if (currentIndex >= PHASE_ORDER.length - 1) {
          return { allowed: false, reason: 'Already at final phase' };
        }

        // For DECIDE phase: ensure cycle has an owner (set during creation)
        // This satisfies the "role assignment" requirement
        // Fallback to createdBy for legacy cycles that don't have ownerId
        if (cycle.currentPhase === 'DECIDE') {
          const hasOwner = (cycle.ownerId && cycle.ownerId.trim() !== '') || 
                          (cycle.createdBy && cycle.createdBy.trim() !== '');
          console.log('[canAdvancePhase] DECIDE phase check - hasOwner:', hasOwner);
          if (!hasOwner) {
            return { 
              allowed: false, 
              reason: 'Cycle must have an owner', 
              missing: ['Cycle owner not set'] 
            };
          }
          // DECIDE → ENGAGE is allowed if owner is set
          // Outcome drivers are optional (can be imported from strategy)
          return { allowed: true };
        }

        const nextPhase = PHASE_ORDER[currentIndex + 1];
        return canTransitionTo(cycle.currentPhase, nextPhase, cycle.outputs);
      },

      getPhaseDefinition: (phase) => PHASE_DEFINITIONS[phase],

      getPhaseProgress: (id) => {
        const cycle = get().getCycle(id);
        if (!cycle) return undefined;
        return getPhaseProgress(cycle);
      },
      
      /**
       * Gate-enforced phase transition (canonical method)
       * Per Build Contract §2.3a: authority transition MUST go through transactional RPC.
       * Calls transition_cycle_phase RPC; on success updates local state from returned cycle.
       */
      transitionPhaseWithGates: async (id, targetPhase, userId, notes) => {
        const cycle = get().getCycle(id);
        if (!cycle) {
          return { success: false, error: 'Cycle not found' };
        }

        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'transitionPhaseWithGates', message: `Cannot transition phase: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return { success: false, error: reasonCode };
        }

        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          return { success: false, error: 'Database unavailable' };
        }

        const { data, error: rpcError } = await supabase.rpc('transition_cycle_phase', {
          p_cycle_id: id,
          p_target_phase: targetPhase,
          p_user_id: userId,
          p_notes: notes ?? null,
        });

        if (rpcError) {
          console.error('[DegapProcessStore] transition_cycle_phase RPC error:', rpcError);
          return { success: false, error: rpcError.message };
        }

        const result = data as { success: boolean; error?: string; blockers?: string[]; cycle?: Record<string, unknown> } | null;
        if (!result || !result.success) {
          return {
            success: false,
            error: result?.error ?? 'Transition failed',
            blockers: result?.blockers,
          };
        }

        if (result.cycle) {
          // Phase C-2: re-apply the cached cycle policy — the RPC returns the
          // raw row whose metadata no longer carries the policy keys.
          const authoritativeCycle = decorateCycleWithPolicy(
            fromDbFormat<DegapCycle>(result.cycle as Record<string, unknown>),
            useSessionPolicyStore.getState().getPolicy('cycle', id)
          );
          set((state) => ({
            cycles: state.cycles.map(c => (c.id === id ? authoritativeCycle : c)),
          }));
        }

        // Fire-and-forget notification for phase transition
        const previousPhase = cycle.currentPhase;
        const recipientIds = (cycle.outputs?.engage?.participants ?? [])
          .map((p: { userId: string }) => p.userId)
          .concat(cycle.ownerId ? [cycle.ownerId] : [])
          .filter((uid: string, idx: number, arr: string[]) => arr.indexOf(uid) === idx);
        const currentUserId = (await import('./userStore')).useUserStore.getState().userId;
        const filteredRecipients = recipientIds.filter((uid: string) => uid !== currentUserId);
        if (filteredRecipients.length > 0) {
          import('../../lib/notificationService').then(({ notifyCyclePhaseChanged }) => {
            notifyCyclePhaseChanged(
              id,
              cycle.name,
              previousPhase,
              targetPhase,
              filteredRecipients,
            ).catch(() => {});
          }).catch(() => {});
        }

        return { success: true };
      },

      // ======================================================================
      // Core B: Parallel Phase Management
      // ======================================================================

      startPhase: async (id, phase, userId) => {
        const cycle = get().getCycle(id);
        if (!cycle) return { success: false, error: 'Cycle not found' };
        
        // Check if phase can be started using authoritative cycle gate state.
        const gateState = await checkCycleGates(id);
        const canStart = canStartPhase(phase, gateState);
        
        if (!canStart.allowed) {
          return { success: false, error: canStart.reason };
        }
        
        const now = new Date();
        
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id !== id) return c;
            
            const updatedPhaseStatuses = {
              ...c.phaseStatuses,
              [phase]: {
                ...c.phaseStatuses[phase],
                status: 'in_progress' as const,
                startedAt: now,
                startedBy: userId,
              },
            };
            
            const newActivePhases = getActivePhases(updatedPhaseStatuses);
            
            // Update legacy currentPhase to primary active phase
            const primaryPhase = getPrimaryPhase(updatedPhaseStatuses);
            
            const merged = {
              ...c,
              phaseStatuses: updatedPhaseStatuses,
              activePhases: newActivePhases,
              currentPhase: primaryPhase,
              updatedAt: now,
            };
            return alignDegapCycleLifecycleStatusWithPhase(merged, now);
          }),
        }));
        
        // Sync to Supabase - AWAIT to ensure write completes
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) {
          await syncCycleToSupabase(updatedCycle, { includeLifecycleStatus: true });
        }
        
        return { success: true };
      },

      completePhase: async (id, phase, userId) => {
        const cycle = get().getCycle(id);
        if (!cycle) return { success: false, error: 'Cycle not found' };
        
        const phaseStatuses = cycle.phaseStatuses || createInitialPhaseStatuses();
        if (phaseStatuses[phase].status !== 'in_progress') {
          return { success: false, error: `Phase ${phase} is not in progress` };
        }
        
        const now = new Date();
        
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id !== id) return c;
            
            const updatedPhaseStatuses = {
              ...c.phaseStatuses,
              [phase]: {
                ...c.phaseStatuses[phase],
                status: 'complete' as const,
                progress: 100,
                completedAt: now,
                completedBy: userId,
              },
            };
            
            const newActivePhases = getActivePhases(updatedPhaseStatuses);
            const primaryPhase = getPrimaryPhase(updatedPhaseStatuses);
            
            // Also update phase history (for backward compatibility)
            const updatedHistory = c.phaseHistory.map((entry) => {
              if (entry.phase === phase && !entry.exitedAt) {
                return { ...entry, exitedAt: now };
              }
              return entry;
            });
            
            const merged = {
              ...c,
              phaseStatuses: updatedPhaseStatuses,
              activePhases: newActivePhases,
              currentPhase: primaryPhase,
              phaseHistory: updatedHistory,
              updatedAt: now,
            };
            return alignDegapCycleLifecycleStatusWithPhase(merged, now);
          }),
        }));
        
        // Sync to Supabase - AWAIT to ensure write completes
        const completedCycle = get().getCycle(id);
        if (completedCycle) {
          await syncCycleToSupabase(completedCycle, { includeLifecycleStatus: true });
        }
        
        return { success: true };
      },

      getActivePhases: (id) => {
        const cycle = get().getCycle(id);
        if (!cycle) return EMPTY_PHASES;
        return cycle.activePhases ?? EMPTY_PHASES;
      },

      getPhaseStatus: (id, phase) => {
        const cycle = get().getCycle(id);
        if (!cycle || !cycle.phaseStatuses) return undefined;
        return cycle.phaseStatuses[phase];
      },

      updatePhaseProgress: async (id, phase, progress) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (updatePhaseProgress):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'updatePhaseProgress', message: `Cannot update phase progress: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id !== id) return c;
            
            const phaseStatuses = c.phaseStatuses || createInitialPhaseStatuses();
            
            return {
              ...c,
              phaseStatuses: {
                ...phaseStatuses,
                [phase]: {
                  ...phaseStatuses[phase],
                  progress: Math.max(0, Math.min(100, progress)),
                },
              },
              updatedAt: new Date(),
            };
          }),
        }));
        
        // Sync to Supabase - AWAIT to ensure write completes
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) {
          await syncCycleToSupabase(updatedCycle);
        }
      },

      // ======================================================================
      // Core B: Aggregation Configuration
      // ======================================================================

      setAggregationConfig: async (id, config) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (setAggregationConfig):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'setAggregationConfig', message: `Cannot set aggregation config: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id !== id) return c;
            
            return {
              ...c,
              aggregationConfig: {
                ...c.aggregationConfig,
                ...config,
              },
              updatedAt: new Date(),
            };
          }),
        }));
        
        // Sync to Supabase - AWAIT to ensure write completes
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) {
          await syncCycleToSupabase(updatedCycle);
        }
      },

      setCycleOwner: async (id, ownerId) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (setCycleOwner):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'setCycleOwner', message: `Cannot set cycle owner: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id !== id) return c;
            
            return {
              ...c,
              ownerId,
              updatedAt: new Date(),
            };
          }),
        }));
        
        // Sync to Supabase - AWAIT to ensure write completes before returning
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) {
          await syncCycleToSupabase(updatedCycle);
        }
      },

      // ======================================================================
      // Status Management
      // ======================================================================

      startCycle: async (id) => {
        const cycle = get().getCycle(id);
        if (!cycle) return { ok: false, error: 'This cycle is not loaded.' };
        // One way to start a cycle (migration 881, roadmap C-5): the Cycle
        // Designer's governed launch, with no design to lay down. The server
        // checks who may start it, moves draft → active, stamps started_at and
        // records the event; the browser only mirrors the result.
        const result = await launchCycleDesign(id, { start_only: true });
        if (!result.ok) {
          return { ok: false, error: result.error ?? 'The cycle could not be started.' };
        }
        set((state) => ({
          cycles: state.cycles.map(c =>
            c.id === id
              ? { ...c, status: 'active' as CycleStatus, startedAt: c.startedAt || new Date(), updatedAt: new Date() }
              : c
          ),
        }));
        return { ok: true };
      },

      resumeCycle: async (id) => {
        const cycle = get().getCycle(id);
        if (!cycle || cycle.status !== 'paused') return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (resumeCycle):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'resumeCycle', message: `Cannot resume cycle: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c =>
            c.id === id
              ? { ...c, status: 'active' as CycleStatus, updatedAt: new Date() }
              : c
          ),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle, { includeLifecycleStatus: true });
      },

      pauseCycle: async (id) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (pauseCycle):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'pauseCycle', message: `Cannot pause cycle: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c =>
            c.id === id
              ? { ...c, status: 'paused' as CycleStatus, updatedAt: new Date() }
              : c
          ),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle, { includeLifecycleStatus: true });
      },

      completeCycle: async (id) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (completeCycle):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'completeCycle', message: `Cannot complete cycle: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }

        // G03: flag DRAFT/CRITERIA_FINALIZED sessions as abandoned. Canon §G03.
        await flagCycleSessionsAbandoned(id, 'cycle_completed');

        set((state) => ({
          cycles: state.cycles.map(c =>
            c.id === id
              ? { ...c, status: 'completed' as CycleStatus, completedAt: new Date(), updatedAt: new Date() }
              : c
          ),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle, { includeLifecycleStatus: true });
      },

      archiveCycle: async (id) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (archiveCycle):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'archiveCycle', message: `Cannot archive cycle: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }

        // G03: flag DRAFT/CRITERIA_FINALIZED sessions as abandoned. Canon §G03.
        await flagCycleSessionsAbandoned(id, 'cycle_archived');

        set((state) => ({
          cycles: state.cycles.map(c =>
            c.id === id
              ? { ...c, status: 'archived' as CycleStatus, updatedAt: new Date() }
              : c
          ),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle, { includeLifecycleStatus: true });
      },

      // ======================================================================
      // Item Management
      // ======================================================================

      addItemsToCycle: async (id, itemIds, options?: { userId?: string; inclusionReason?: string; note?: string }) => {
        const cycle = get().getCycle(id);
        if (!cycle) {
          console.error('[DegapProcessStore] Cannot add items: cycle not found', id);
          return;
        }
        const cycleData = { createdBy: cycle.createdBy, ownerId: cycle.ownerId };
        const policyDecision = await checkPolicyForCycle('cycle.pool.add', id, cycleData);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for cycle.pool.add:', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'addItemsToCycle', message: `Cannot add to cycle pool: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        // Update local state
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              const newItemIds = [...new Set([...c.itemIds, ...itemIds])];
              // Also update shapedItemIds to include the new items
              // (items added via Gather are pre-filtered to be ready)
              const newShapedIds = [...new Set([...c.outputs.gather.shapedItemIds, ...itemIds])];
              return { 
                ...c, 
                itemIds: newItemIds, 
                updatedAt: new Date(),
                outputs: {
                  ...c.outputs,
                  gather: {
                    ...c.outputs.gather,
                    itemCount: newItemIds.length,
                    shapedItemIds: newShapedIds,
                    readyForArrangeCount: newShapedIds.length,
                  },
                },
              };
            }
            return c;
          }),
        }));
        
        // Sync degap_cycles to Supabase - AWAIT to ensure write completes
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
        
        // WRITE PATH: Also insert into cycle_item_participation for proper provenance
        // Per Build Contract: "Every new concept must include Schema + Write path + Read path"
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          const now = new Date().toISOString();
          const userId = options?.userId || cycle.ownerId || cycle.createdBy;
          const inclusionReason = options?.inclusionReason || 'manual_selection';
          
          // Insert each item into cycle_item_participation
          const participationRows = itemIds.map(itemId => ({
            cycle_id: id,
            item_id: itemId,
            inclusion_reason: inclusionReason,
            inclusion_note: options?.note || null,
            cycle_status: 'confirmed', // Items added via Gather are ready
            added_at: now,
            added_by: userId,
            confirmed_at: now,
            confirmed_by: userId,
            workspace_id: cycle.workspaceId || null,
          }));
          
          // Upsert to handle re-adding items - AWAIT to ensure DB confirmation
          const { error: participationError } = await supabase
            .from('cycle_item_participation')
            .upsert(participationRows, { 
              onConflict: 'cycle_id,item_id',
              ignoreDuplicates: false 
            });
          
          if (participationError) {
            console.warn('[DegapProcessStore] Failed to write cycle_item_participation:', participationError.message);
          } else {
            console.log(`[DegapProcessStore] Added ${itemIds.length} items to cycle_item_participation`);
            const actorId = options?.userId || cycle.ownerId || cycle.createdBy || 'system';
            try {
              for (const itemId of itemIds) {
                useAuditStore.getState().logEvent({
                  type: 'item_added_to_cycle',
                  itemId,
                  actorId,
                  actorRole: 'cycle-admin',
                  note: `Added to cycle`,
                  details: { cycleId: id, inclusionReason: options?.inclusionReason || 'manual_selection' },
                } as unknown as Parameters<ReturnType<typeof useAuditStore.getState>['logEvent']>[0]);
              }
            } catch (_) { /* best-effort */ }
          }
          
          // Also sync to cycle_comparison_sets for candidate pool tracking
          // Per Build Contract: candidate pool selection is a persisted state transition
          const updatedCycleForSync = get().getCycle(id);
          if (updatedCycleForSync) {
            const allItemIds = updatedCycleForSync.itemIds;
            const { error: comparisonError } = await supabase
              .from('cycle_comparison_sets')
              .upsert({
                cycle_id: id,
                item_ids: allItemIds,
                updated_at: now,
              }, { onConflict: 'cycle_id' });
            
            if (comparisonError) {
              console.warn('[DegapProcessStore] Failed to sync cycle_comparison_sets:', comparisonError.message);
            } else {
              console.log(`[DegapProcessStore] Synced ${allItemIds.length} items to cycle_comparison_sets`);
            }
          }
        }
      },

      removeItemsFromCycle: async (id, itemIds, options?: { userId?: string; reason?: string }) => {
        const cycle = get().getCycle(id);
        if (!cycle) {
          console.error('[DegapProcessStore] Cannot remove items: cycle not found', id);
          return;
        }
        const cycleData = { createdBy: cycle.createdBy, ownerId: cycle.ownerId };
        const policyDecision = await checkPolicyForCycle('cycle.pool.remove', id, cycleData);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code ?? 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for cycle.pool.remove:', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'removeItemsFromCycle', message: `Cannot remove from cycle pool: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        // Update local state
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              const newItemIds = c.itemIds.filter(itemId => !itemIds.includes(itemId));
              return {
                ...c,
                itemIds: newItemIds,
                updatedAt: new Date(),
                outputs: {
                  ...c.outputs,
                  gather: {
                    ...c.outputs.gather,
                    itemCount: newItemIds.length,
                    shapedItemIds: c.outputs.gather.shapedItemIds.filter(i => !itemIds.includes(i)),
                  },
                },
              };
            }
            return c;
          }),
        }));
        
        // Sync degap_cycles to Supabase - AWAIT to ensure write completes
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
        
        // WRITE PATH: Mark items as excluded in cycle_item_participation (preserve provenance)
        // Per Build Contract 2.5: "Silent overwrite is explicitly forbidden"
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          const now = new Date().toISOString();
          const userId = options?.userId || cycle.ownerId || cycle.createdBy;
          
          // Update each item to excluded status - AWAIT to ensure write completes
          const { error } = await supabase
            .from('cycle_item_participation')
            .update({
              cycle_status: 'excluded',
              excluded_at: now,
              excluded_by: userId,
              excluded_reason: options?.reason || 'Removed from cycle',
            })
            .eq('cycle_id', id)
            .in('item_id', itemIds);
          
          if (error) {
            console.warn('[DegapProcessStore] Failed to update cycle_item_participation:', error.message);
          } else {
            console.log(`[DegapProcessStore] Marked ${itemIds.length} items as excluded in cycle_item_participation`);
            const actorId = options?.userId || cycle.ownerId || cycle.createdBy || 'system';
            try {
              for (const itemId of itemIds) {
                useAuditStore.getState().logEvent({
                  type: 'item_removed_from_cycle',
                  itemId,
                  actorId,
                  actorRole: 'cycle-admin',
                  note: options?.reason || 'Removed from cycle',
                  details: { cycleId: id },
                } as unknown as Parameters<ReturnType<typeof useAuditStore.getState>['logEvent']>[0]);
              }
            } catch (_) { /* best-effort */ }
          }
        }
      },

      getCycleItems: (id) => {
        const cycle = get().getCycle(id);
        return cycle?.itemIds ?? EMPTY_ITEM_IDS;
      },
      
      // ======================================================================
      // EGAp Iteration Management
      // ======================================================================
      
      getOrCreateActiveIteration: async (id, createdBy) => {
        const cycle = get().getCycle(id);
        if (!cycle) {
          throw new Error('Cycle not found');
        }
        
        // First, check if there's already an active iteration
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          const { data, error } = await supabase
            .from('egap_iterations')
            .select('id')
            .eq('cycle_id', id)
            .eq('status', 'in_progress')
            .order('created_at', { ascending: false })
            .limit(1)
            .single();
          
          if (data && !error) {
            return data.id;
          }
        }
        
        // No active iteration found - create one
        // Import stores dynamically to avoid circular dependencies
        const { useEGApIterationStore } = await import('./egapIterationStore');
        const { useTrackStore } = await import('./trackStore');
        const { createTrackWithFeedback } = await import('../../lib/tracks/createTrackFlow');

        // v2.3: Iterations require a track_id. Get or create the default track.
        const trackStore = useTrackStore.getState();
        let defaultTrack = trackStore.getDefaultTrack(id);

        if (!defaultTrack) {
          // Create the default track if it doesn't exist
          const trackResult = await createTrackWithFeedback({
            cycleId: id,
            name: cycle.name,
            description: 'Default comparability lane for this cycle',
            scopeType: 'default',
            scopeLabel: 'Default',
          });

          if (!trackResult.ok || !trackResult.track) {
            throw new Error(trackResult.error || 'Failed to create default track for iteration');
          }
          defaultTrack = trackResult.track;
        }
        
        const iteration = await useEGApIterationStore.getState().createIteration({
          cycleId: id,
          trackId: defaultTrack.id,
          scopeLevel: 'enterprise',
        }, createdBy);
        
        // Update cycle with active iteration ID
        if (supabase) {
          await supabase
            .from('degap_cycles')
            .update({ active_egap_iteration_id: iteration.id })
            .eq('id', id);
        }
        
        return iteration.id;
      },
      
      getCycleGateStatus: async (id) => {
        return await checkCycleGates(id);
      },

      // ======================================================================
      // Phase Outputs - DECIDE
      // ======================================================================

      addOutcomeDriver: async (id, driver) => {
        const cycle = get().cycles.find(c => c.id === id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (addOutcomeDriver):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'addOutcomeDriver', message: `Cannot add outcome driver: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        const updated = {
          ...cycle,
          outputs: {
            ...cycle.outputs,
            decide: {
              ...cycle.outputs.decide,
              outcomeDrivers: [...cycle.outputs.decide.outcomeDrivers, driver],
            },
          },
          updatedAt: new Date(),
        };
        
        set((state) => ({
          cycles: state.cycles.map(c => c.id === id ? updated : c),
        }));
        
        // Sync to Supabase - AWAIT to ensure write completes
        if (isSupabaseAvailable()) {
          const result = await updateRecord<DegapCycle>(TABLE_NAME, id, updated);
          if (result.error) {
            console.error('[DegapProcessStore] Failed to sync outcome driver to Supabase:', result.error);
          }
        }
      },

      removeOutcomeDriver: async (id, driver) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (removeOutcomeDriver):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'removeOutcomeDriver', message: `Cannot remove outcome driver: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  decide: {
                    ...c.outputs.decide,
                    outcomeDrivers: c.outputs.decide.outcomeDrivers.filter(d => d !== driver),
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      importDriversFromStrategy: async (id) => {
        const cycle = get().getCycle(id);
        if (!cycle) return { imported: 0 };
        
        // Get linked strategy using the strategy store
        const strategyStore = useStrategyStore.getState();
        const strategy = strategyStore.getStrategyForCycle(id);
        
        if (!strategy) {
          console.log('[DegapProcessStore] No linked strategy found for cycle:', id);
          return { imported: 0 };
        }
        
        // Get strategic drivers from the strategy
        const strategicDrivers = strategy.strategicDrivers || [];
        if (strategicDrivers.length === 0) {
          console.log('[DegapProcessStore] Strategy has no drivers:', strategy.name);
          return { imported: 0, strategyName: strategy.name };
        }
        
        // Get existing outcome drivers to avoid duplicates
        const existingDrivers = new Set(cycle.outputs.decide.outcomeDrivers);
        
        // Import driver names as outcome drivers
        const driversToImport = strategicDrivers
          .filter(d => d.isActive)
          .map(d => d.name)
          .filter(name => !existingDrivers.has(name));
        
        if (driversToImport.length === 0) {
          console.log('[DegapProcessStore] All drivers already imported');
          return { imported: 0, strategyName: strategy.name };
        }
        
        // Update the cycle with imported drivers
        const updated = {
          ...cycle,
          outputs: {
            ...cycle.outputs,
            decide: {
              ...cycle.outputs.decide,
              outcomeDrivers: [...cycle.outputs.decide.outcomeDrivers, ...driversToImport],
            },
          },
          // Also set strategyRef if not already set
          strategyRef: cycle.strategyRef || {
            id: strategy.id,
            name: strategy.name,
            source: 'internal',
          },
          updatedAt: new Date(),
        };
        
        set((state) => ({
          cycles: state.cycles.map(c => c.id === id ? updated : c),
        }));
        
        // Sync to Supabase - AWAIT to ensure write completes
        await syncCycleToSupabase(updated);
        
        console.log(`[DegapProcessStore] Imported ${driversToImport.length} drivers from strategy:`, strategy.name);
        return { imported: driversToImport.length, strategyName: strategy.name };
      },

      setClarityDocument: async (id, document) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (setClarityDocument):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'setClarityDocument', message: `Cannot set clarity document: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  decide: { ...c.outputs.decide, clarityDocument: document },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      setDomainBoundaries: async (id, boundaries) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (setDomainBoundaries):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'setDomainBoundaries', message: `Cannot set domain boundaries: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  decide: { ...c.outputs.decide, domainBoundaries: boundaries },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      addRoleAssignment: async (id, assignment) => {
        const cycle = get().cycles.find(c => c.id === id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (addRoleAssignment):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'addRoleAssignment', message: `Cannot add role assignment: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        const updated = {
          ...cycle,
          outputs: {
            ...cycle.outputs,
            decide: {
              ...cycle.outputs.decide,
              roleAssignments: [...cycle.outputs.decide.roleAssignments, { ...assignment, assignedAt: new Date() }],
            },
          },
          updatedAt: new Date(),
        };
        
        set((state) => ({
          cycles: state.cycles.map(c => c.id === id ? updated : c),
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          updateRecord<DegapCycle>(TABLE_NAME, id, updated).then(result => {
            if (result.error) {
              console.error('[DegapProcessStore] Failed to sync role assignment to Supabase:', result.error);
            }
          });
        }
      },

      // ======================================================================
      // Phase Outputs - ENGAGE
      // ======================================================================

      /**
       * @deprecated Use addParticipantToTable() instead. This writes to JSONB.
       */
      addParticipant: async (id, participant) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (addParticipant):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'addParticipant', message: `Cannot add participant: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  engage: {
                    ...c.outputs.engage,
                    participants: [...c.outputs.engage.participants, { ...participant, joinedAt: new Date() }],
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      /**
       * @deprecated Use removeParticipantFromTable() instead. This writes to JSONB.
       */
      removeParticipant: async (id, participantUserId) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (removeParticipant):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'removeParticipant', message: `Cannot remove participant: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  engage: {
                    ...c.outputs.engage,
                    participants: c.outputs.engage.participants.filter(p => p.userId !== participantUserId),
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      addScheduledSession: async (id, sessionId) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (addScheduledSession):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'addScheduledSession', message: `Cannot add scheduled session: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                sessionIds: [...new Set([...c.sessionIds, sessionId])],
                outputs: {
                  ...c.outputs,
                  engage: {
                    ...c.outputs.engage,
                    scheduledSessions: [...c.outputs.engage.scheduledSessions, sessionId],
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      setDecisionRights: async (id, rights) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (setDecisionRights):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'setDecisionRights', message: `Cannot set decision rights: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  engage: { ...c.outputs.engage, decisionRights: rights },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      // ======================================================================
      // Phase Outputs - GATHER
      // ======================================================================

      markItemShaped: async (id, itemId) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('items.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for items.edit (markItemShaped):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'markItemShaped', message: `Cannot mark item work complete: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id && !c.outputs.gather.shapedItemIds.includes(itemId)) {
              const newShapedIds = [...c.outputs.gather.shapedItemIds, itemId];
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  gather: {
                    ...c.outputs.gather,
                    shapedItemIds: newShapedIds,
                    readyForArrangeCount: newShapedIds.length,
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      updateGatherStats: async (id) => {
        // This would typically be called with external item data
        // For now, just update based on what we have
        const cycle = get().getCycle(id);
        if (!cycle) return;

        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  gather: {
                    ...c.outputs.gather,
                    itemCount: c.itemIds.length,
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      // ======================================================================
      // Phase Outputs - ARRANGE
      // ======================================================================

      addFocusList: async (id, focusListId) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (addFocusList):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'addFocusList', message: `Cannot add focus list: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                itemPoolIds: [...new Set([...c.itemPoolIds, focusListId])],
                outputs: {
                  ...c.outputs,
                  arrange: {
                    ...c.outputs.arrange,
                    itemPoolIds: [...new Set([...c.outputs.arrange.itemPoolIds, focusListId])],
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      recordToolRun: async (id, toolRun) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.edit', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.edit (recordToolRun):', reasonCode);
          window.dispatchEvent(new CustomEvent('authorization-denied', {
            detail: { store: 'degapProcess', action: 'recordToolRun', message: `Cannot record tool run: ${reasonCode}`, decision: policyDecision },
          }));
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              const newToolRun: ToolRunRecord = {
                ...toolRun,
                id: generateUUID(),
                runAt: new Date(),
              };
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  arrange: {
                    ...c.outputs.arrange,
                    toolRuns: [...c.outputs.arrange.toolRuns, newToolRun],
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      addSnapshot: async (id, snapshotId) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.prioritize', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.prioritize (addSnapshot):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'addSnapshot', message: `Cannot add snapshot: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                snapshotIds: [...new Set([...c.snapshotIds, snapshotId])],
                outputs: {
                  ...c.outputs,
                  arrange: {
                    ...c.outputs.arrange,
                    snapshotIds: [...new Set([...c.outputs.arrange.snapshotIds, snapshotId])],
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      // ======================================================================
      // Phase Outputs - PRIORITIZE
      // ======================================================================

      publishSnapshot: async (id, snapshotId) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('process.publish', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for process.publish (publishSnapshot):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'publishSnapshot', message: `Cannot publish snapshot: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  prioritize: {
                    ...c.outputs.prioritize,
                    publishedSnapshotId: snapshotId,
                    publishedAt: new Date(),
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      setCutLine: async (id, position) => {
        const cycle = get().getCycle(id);
        if (!cycle) return;
        const policyDecision = await checkPolicyForCycle('lists.move_cutline', id, cycle as unknown as Record<string, unknown>);
        if (!policyDecision.allowed) {
          const reasonCode = policyDecision.reasons[0]?.code || 'DENIED';
          console.warn('[DegapProcessStore] Policy denied for lists.move_cutline (setCutLine):', reasonCode);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('authorization-denied', {
              detail: { store: 'degapProcess', action: 'setCutLine', message: `Cannot set cut line: ${reasonCode}`, decision: policyDecision },
            }));
          }
          return;
        }
        set((state) => ({
          cycles: state.cycles.map(c => {
            if (c.id === id) {
              return {
                ...c,
                outputs: {
                  ...c.outputs,
                  prioritize: {
                    ...c.outputs.prioritize,
                    cutLinePosition: position,
                  },
                },
                updatedAt: new Date(),
              };
            }
            return c;
          }),
        }));
        const updatedCycle = get().getCycle(id);
        if (updatedCycle) await syncCycleToSupabase(updatedCycle);
      },

      // ======================================================================
      // Backward Compatibility Aliases
      // ======================================================================

      get processes() { return get().cycles; },
      get activeProcessId() { return get().activeCycleId; },
      
      createProcess: (data) => get().createCycle({ 
        name: data.name, 
        description: data.description, 
        createdBy: data.ownerId 
      }),
      updateProcess: (id, updates) => get().updateCycle(id, updates),
      deleteProcess: (id) => get().deleteCycle(id),
      setActiveProcess: (id) => get().setActiveCycle(id),
      getProcess: (id) => get().getCycle(id),
      getActiveProcess: () => get().getActiveCycle(),

      canTransitionPhase: (id, targetPhase) => {
        const cycle = get().getCycle(id);
        if (!cycle) return false;
        const result = canTransitionTo(cycle.currentPhase, targetPhase, cycle.outputs);
        return result.allowed;
      },
      
      addItemsToProcess: (id, itemIds) => get().addItemsToCycle(id, itemIds),
      removeItemsFromProcess: (id, itemIds) => get().removeItemsFromCycle(id, itemIds),
      getProcessItems: (id) => get().getCycleItems(id),
    }));

/**
 * Neutral alias for the legacy DEGAP cycle store hook.
 * Preserves compatibility while allowing call sites to move toward cycle-first terminology.
 */
export const useCycleStore = useDegapProcessStore;
